import {startOfDay, endOfDay, eachDayOfInterval, addDays} from 'date-fns';
import {WorkdayStatus, LocalDateTimeString} from '../apiTypes';
import {dateToLocalDateString, dateToLocalDateTimeString, dateKeyForDateTimeString} from '../dateTimeHelpers';
import {Api} from './api';

const ROW_TEMPLATE = {
  StartDate: '',
  EndDate: '',
  WorkdayStatus: <WorkdayStatus>'Unknown',
  BuildingName: <string | null>null,
  FloorName: <string | null>null,
  AreaName: <string | null>null,
  DeskName: <string | null>null,
  ParkingLotName: <string | null>null,
  ParkingZoneName: <string | null>null,
};
export async function TEMP_constructOverviewOnTheClient(
  from: Date,
  to: Date,
  reservationsApi: Api,
  resolveParkingReservations: (q: {from: LocalDateTimeString; to: LocalDateTimeString}) => Promise<ParkingDto[]>,
) {
  // Construct a row for every date
  const start = startOfDay(from);
  const end = endOfDay(to);

  const yearIndex = Object.fromEntries(
    eachDayOfInterval({start, end})
      .reverse()
      .map((date) => {
        const dateKey = dateToLocalDateString(date);
        return [
          dateKey,
          {
            ...ROW_TEMPLATE,
            StartDate: dateKey,
            EndDate: dateKey,
          },
        ];
      }),
  );

  // Decide on the number of calls to make
  const SLICE = 29;
  const requestRanges = eachDayOfInterval({start, end}, {step: SLICE}).map((date) => {
    const from = dateToLocalDateTimeString(date);
    const to = dateToLocalDateTimeString(endOfDay(addDays(date, SLICE)));

    return {from, to};
  });

  // Make the calls required
  const [workdays, reservations, parkings] = await Promise.all([
    // Workdays
    await Promise.all(
      requestRanges.map((q) => {
        return reservationsApi.getMyWorkdays(q);
      }),
    ).then((slices) => slices.flat()),

    // Workspace reservations
    await Promise.all(requestRanges.map((q) => reservationsApi.getMyWorkspaceReservations(q))).then((slices) =>
      slices.flat().filter((r) => r.status === 'Confirmed'),
    ),

    // Parking reservations
    await Promise.all(requestRanges.map((q) => resolveParkingReservations(q))).then((slices) => slices.flat()),
  ]);

  // Process workdays
  for (const wd of workdays) {
    const entry = yearIndex[dateKeyForDateTimeString(wd.localStart)];
    if (!entry) continue;

    entry.WorkdayStatus = wd.status;
    if (wd.status === 'OfficeDay') {
      entry.BuildingName = wd.buildingName;
    }
  }

  // Process workspace reservations
  for (const r of reservations) {
    const entry = yearIndex[dateKeyForDateTimeString(r.localStart)];
    if (!entry) continue;

    if ('floorName' in r.workspace) entry.FloorName = r.workspace.floorName;
    if ('areaName' in r.workspace) entry.AreaName = r.workspace.areaName;
    if ('deskName' in r.workspace) entry.DeskName = r.workspace.deskName;
  }

  // Process parking reservations
  for (const p of parkings) {
    const entry = yearIndex[dateKeyForDateTimeString(p.date.slice(0, 'yyyy-MM-dd'.length))];
    if (!entry) continue;

    entry.ParkingZoneName = p.zoneName;
    entry.ParkingLotName = p.parkingLotName;
  }

  // Collect results
  const filename = `booking_data_from_${dateToLocalDateString(start)}_to_${dateToLocalDateString(end)}.csv`;

  const headers = Object.keys(ROW_TEMPLATE).join(',');
  const csv = `sep=,\n${headers}\n${Object.values(yearIndex).map(rowToString).join('\n')}`;

  return Promise.resolve({filename, data: csv});
}
const rowToString = (row: typeof ROW_TEMPLATE) => Object.values(row).map(escapeCSVField).join(',');
function escapeCSVField(field: number | string | null) {
  if (field == null) return '';
  const str = field.toString();
  const needsQuotes = /[",\n]/.test(str);
  const escaped = str.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}
// Importing this breaks everything because of some utils import inside the file this type is in
export type ParkingDto = {
  id: string;
  date: string;
  nodeId: string;
  parkingLotId: string;
  zoneId: string;
  parkingLotName: string;
  zoneName: string;
};
